import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  createGenerateClassName,
  createTheme,
  Grid,
  StylesProvider,
  ThemeProvider,
  Typography,
} from "@material-ui/core";
import trackEvent from "../utils/appMetrics";

// SplashLanding is self-contained — it carries its own MUI styles and
// theme so it can be dropped into any view (home page, login page,
// post-OAuth signup) without the caller wiring up providers.
const splashGenerateClassName = createGenerateClassName({
  productionPrefix: "c",
  disableGlobal: true,
  seed: "splash",
});

const splashTheme = createTheme({
  typography: { fontFamily: "'Nunito', 'Varela Round', sans-serif" },
});

// ====================================================================
// Shared logged-out splash. Rendered behind the home page (app.swyvel.io)
// and behind the login page. Most visitors arrive straight from
// swyvel.io, so this is warm traffic — lean and conversion-tight: one
// studio-owner hero, three concrete proof points, one ask. Shares the
// black/white design system with the Features page.
// ====================================================================
const SPLASH_INK = "#1a1a1a";
const SPLASH_MUTE = "#666666";
const SPLASH_LINE = "#E0E0E0";
const SPLASH_BLUE = "#007BFF";

const splashCard = {
  background: "#fff",
  border: `1px solid ${SPLASH_LINE}`,
  borderRadius: 16,
  boxShadow: "0 4px 6px rgba(0,0,0,.05)",
};

const splashPrimaryCTA = {
  background: SPLASH_INK,
  color: "#fff",
  border: "none",
  borderRadius: 140,
  padding: "14px 30px",
  fontSize: 16,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "'Nunito', 'Varela Round', sans-serif",
};

const splashSecondaryCTA = {
  background: "#fff",
  color: SPLASH_INK,
  border: `1px solid ${SPLASH_INK}`,
  borderRadius: 140,
  padding: "13px 28px",
  fontSize: 16,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "'Nunito', 'Varela Round', sans-serif",
};

// A faux app window — shows the real product so bottom-of-funnel
// visitors see what they're signing up for. Built from design tokens,
// so it never goes stale. To swap in a video, replace the dashboard
// body below with a muted, looping <video> framed by the same window.
// Emotion-first hero visual: lead with the dancer (the feeling), with a small
// "live" product card resting on the photo for proof. Beats the old finance
// dashboard, which read as generic gym software under a "dance" headline.
// A silent 11s brand film (ballet → ballroom → hip-hop, one dark stage) plays
// once over the static, then fades back to it. The static stays underneath the
// whole time, so blocked autoplay, load errors, and reduced-motion users all
// land on exactly the old hero.
const phCapture = (event, props = {}) => {
  if (typeof window !== "undefined" && window.posthog) window.posthog.capture(event, props);
};

const HeroDanceVisual = () => {
  const videoRef = useRef(null);
  const [videoDone, setVideoDone] = useState(false);
  const [videoOk, setVideoOk] = useState(true);

  const showVideo =
    videoOk &&
    typeof window !== "undefined" &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (!showVideo || !videoRef.current) return;
    const el = videoRef.current;
    // React can drop the muted attribute from the DOM, which breaks autoplay
    // policies — set the property directly, then treat a rejected play() as
    // "no video" and leave the static showing.
    el.muted = true;
    const attempt = el.play();
    if (attempt && attempt.then) {
      attempt
        .then(() => phCapture("hero_video_started"))
        .catch(() => {
          setVideoOk(false);
          phCapture("hero_video_fallback", { reason: "autoplay_blocked" });
        });
    }
  }, [showVideo]);

  return (
  <Box style={{ position: "relative", width: "100%", maxWidth: 460, margin: "0 auto" }}>
    <div
      style={{
        position: "relative",
        borderRadius: 20,
        overflow: "hidden",
        aspectRatio: "4 / 5",
        background: "#0d101c",
        boxShadow: "0 24px 50px -16px rgba(15,23,42,.4)",
      }}
    >
      <img
        src="/marketing/dance-hero.jpg"
        alt="A dancer mid-performance"
        style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "58% center", display: "block" }}
      />
      {showVideo && (
        <video
          ref={videoRef}
          src="/marketing/video_hero.mp4"
          muted
          playsInline
          autoPlay
          preload="auto"
          onEnded={() => {
            setVideoDone(true);
            phCapture("hero_video_completed");
          }}
          onError={() => {
            setVideoOk(false);
            phCapture("hero_video_fallback", { reason: "load_error" });
          }}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "58% center",
            pointerEvents: "none",
            opacity: videoDone ? 0 : 1,
            transition: "opacity .9s ease",
          }}
        />
      )}
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(13,16,28,0) 48%, rgba(13,16,28,.58) 100%)" }} />
      <span
        style={{
          position: "absolute",
          top: 14,
          left: 14,
          background: "rgba(255,255,255,.18)",
          border: "1px solid rgba(255,255,255,.4)",
          backdropFilter: "blur(6px)",
          color: "#fff",
          borderRadius: 140,
          padding: "5px 12px",
          fontSize: 11,
          fontWeight: 600,
        }}
      >
        Spring Recital · 18 days
      </span>

      {/* Floating product-proof card — "it's real software" without stealing the emotion. */}
      <Box
        style={{
          position: "absolute",
          left: 14,
          right: 14,
          bottom: 14,
          background: "#fff",
          border: `1px solid ${SPLASH_LINE}`,
          borderRadius: 14,
          padding: "11px 13px",
          boxShadow: "0 12px 28px -10px rgba(15,23,42,.35)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: SPLASH_INK }}>Sunrise Dance Studio</div>
          <div style={{ fontSize: 10, color: SPLASH_MUTE }}>Today's classes</div>
        </div>
        {[
          { dot: "#EC4899", name: "Ballet · Level 3", meta: "4:00 PM · Ms. Elena", tag: "Full", full: true },
          { dot: "#8B5CF6", name: "Contemporary · Adv.", meta: "6:15 PM · Priya", tag: "9/12", full: false },
        ].map((c, i) => (
          <div
            key={c.name}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              paddingTop: i === 0 ? 0 : 7,
              marginTop: i === 0 ? 0 : 7,
              borderTop: i === 0 ? "none" : `1px solid ${SPLASH_LINE}`,
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: 2, background: c.dot, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: SPLASH_INK }}>{c.name}</div>
              <div style={{ fontSize: 10, color: SPLASH_MUTE }}>{c.meta}</div>
            </div>
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                color: c.full ? "#065F46" : SPLASH_MUTE,
                background: c.full ? "#ECFDF5" : "transparent",
                border: `1px solid ${c.full ? "#A7F3D0" : "transparent"}`,
                borderRadius: 140,
                padding: "2px 8px",
                flexShrink: 0,
              }}
            >
              {c.tag}
            </span>
          </div>
        ))}
      </Box>
    </div>
  </Box>
  );
};

// Line-art icons, inlined so the splash carries no emoji and no
// icon-font dependency. Each sits in a soft tile, matching the
// in-app "quick action" icon treatment.
const IconGlobe = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10Z" />
  </svg>
);

const IconCard = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="5" width="20" height="14" rx="2.5" />
    <path d="M2 10h20" />
    <path d="M6 14.5h4" />
  </svg>
);

const IconMegaphone = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m3 11 18-5v12L3 14v-3Z" />
    <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
  </svg>
);

const IconTile = ({ children, accent = SPLASH_INK, tint = "#F4F6F8" }) => (
  <Box
    style={{
      width: 50,
      height: 50,
      borderRadius: 13,
      background: tint,
      color: accent,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 16,
    }}
  >
    {children}
  </Box>
);

// Mini product snippets — "show, don't tell." Each card opens with a small,
// legible slice of the real product surface it describes, themed to its accent.
const SnippetFrame = ({ children }) => (
  <Box
    style={{
      background: "#FAFBFC",
      border: `1px solid ${SPLASH_LINE}`,
      borderRadius: 12,
      padding: 12,
      marginBottom: 18,
      minHeight: 142,
    }}
  >
    {children}
  </Box>
);

const SnippetBooking = () => (
  <SnippetFrame>
    <div style={{ fontSize: 11, fontWeight: 700, color: SPLASH_INK }}>Sunrise Dance Studio</div>
    <div style={{ fontSize: 9, color: SPLASH_MUTE, marginBottom: 10 }}>Book a class</div>
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 9px", background: "#fff", border: `1px solid ${SPLASH_LINE}`, borderRadius: 8, marginBottom: 10 }}>
      <span style={{ width: 6, height: 6, borderRadius: 2, background: "#EC4899", flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10.5, fontWeight: 600, color: SPLASH_INK }}>Ballet · Beginner</div>
        <div style={{ fontSize: 9, color: SPLASH_MUTE }}>Tue · 4:00 PM</div>
      </div>
    </div>
    <div style={{ background: "#007BFF", color: "#fff", textAlign: "center", fontSize: 11, fontWeight: 600, borderRadius: 140, padding: "8px 0" }}>
      Book a free trial
    </div>
  </SnippetFrame>
);

const SnippetInvoice = () => (
  <SnippetFrame>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 9 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: SPLASH_INK }}>The Patel Family</span>
      <span style={{ fontSize: 9, fontWeight: 700, color: "#065F46", background: "#ECFDF5", border: "1px solid #A7F3D0", borderRadius: 140, padding: "2px 8px" }}>Paid</span>
    </div>
    {[["Tuition · 2 dancers", "$260"], ["Sibling discount", "−$20"]].map(([k, v]) => (
      <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: SPLASH_MUTE, padding: "3px 0" }}>
        <span>{k}</span>
        <span>{v}</span>
      </div>
    ))}
    <div style={{ display: "flex", justifyContent: "space-between", borderTop: `1px solid ${SPLASH_LINE}`, marginTop: 6, paddingTop: 8, fontSize: 12, fontWeight: 700, color: SPLASH_INK }}>
      <span>Charged to card</span>
      <span>$240</span>
    </div>
  </SnippetFrame>
);

const SnippetCheckin = () => (
  <SnippetFrame>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 9 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: SPLASH_INK }}>Ballet · Level 3</span>
      <span style={{ fontSize: 9, color: SPLASH_MUTE }}>3:00 PM</span>
    </div>
    {["Ava M.", "Sofia R.", "Mia T."].map((n) => (
      <div key={n} style={{ display: "flex", alignItems: "center", gap: 7, padding: "2.5px 0", fontSize: 10.5, color: SPLASH_INK }}>
        <span style={{ width: 14, height: 14, borderRadius: "50%", background: "#ECFDF5", border: "1px solid #A7F3D0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
        </span>
        {n}
      </div>
    ))}
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 9.5, color: "#EC4899", fontWeight: 600 }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
      Reminder texted to 12 families
    </div>
  </SnippetFrame>
);

const splashProofPoints = [
  {
    Snippet: SnippetBooking,
    accent: "#007BFF",
    title: "A free booking page",
    body: "Every studio gets a public page with your classes and brand. Parents browse the schedule and request a trial — no phone tag.",
  },
  {
    Snippet: SnippetInvoice,
    accent: "#10B981",
    title: "Tuition that bills itself",
    body: "One monthly invoice per family — pro-rated, sibling discounts applied, charged to the card on file. Stop chasing checks.",
  },
  {
    Snippet: SnippetCheckin,
    accent: "#EC4899",
    title: "Attendance + marketing in one place",
    body: "Check students in from any phone, then keep them coming back with built-in email, SMS, and review collection.",
  },
];

const SplashLandingContent = ({ onStartTrial = () => {} }) => {
  const goLogin = () => {
    window.location = "/users/login";
  };
  const startTrial = (source) => {
    trackEvent("sign_up_cta_clicked", { source });
    // Top-of-funnel signal for Meta — fires when a visitor shows
    // intent (opens the signup modal) but before they submit it.
    if (typeof window !== "undefined" && window.fbq) {
      window.fbq("track", "Lead", { content_category: source });
    }
    onStartTrial();
  };

  return (
    <Box style={{ background: "#fff", fontFamily: "'Nunito', 'Varela Round', sans-serif" }}>
      {/* Hero */}
      <Box style={{ background: "#FAFAFA", borderBottom: `1px solid ${SPLASH_LINE}`, padding: "72px 20px" }}>
        <Container>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              {/* Bare kicker, not a pill: an enclosed rounded shape at the top
                  of the hero reads as a button and competes with the CTA. */}
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: SPLASH_BLUE,
                  marginBottom: 18,
                }}
              >
                For dance studio owners
              </div>
              <Typography
                style={{ fontSize: 44, fontWeight: 600, color: SPLASH_INK, lineHeight: 1.15, marginBottom: 16 }}
              >
                Run your dance studio, not the busywork
              </Typography>
              <Typography
                style={{ fontSize: 18, color: "#4F4F4F", lineHeight: 1.6, marginBottom: 28, maxWidth: 460 }}
              >
                Bookings, tuition, follow-ups and enrollments in one place — and nothing leaks:
                every inquiry gets a follow-up, every renewal gets caught, every dollar lands on the books.
              </Typography>
              <Box style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <button style={splashPrimaryCTA} onClick={() => startTrial("homepage_hero")}>
                  Start free
                </button>
                <button style={splashSecondaryCTA} onClick={goLogin}>
                  Log in
                </button>
              </Box>
              <div style={{ fontSize: 13, color: SPLASH_MUTE, marginTop: 14 }}>
                14 days free · no credit card required
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box style={{ display: "flex", justifyContent: "center" }}>
                <HeroDanceVisual />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Proof points */}
      <Box style={{ padding: "72px 20px" }}>
        <Container>
          <Typography
            style={{ fontSize: 30, fontWeight: 600, color: SPLASH_INK, textAlign: "center", marginBottom: 8 }}
          >
            The business side, handled
          </Typography>
          <Typography style={{ fontSize: 16, color: SPLASH_MUTE, textAlign: "center", marginBottom: 40 }}>
            One login replaces the spreadsheets, the booking calls, and the payment chasing.
          </Typography>
          <Box style={{ display: "flex", flexWrap: "wrap", gap: 24, justifyContent: "center" }}>
            {splashProofPoints.map((p) => {
              const { Snippet } = p;
              return (
                <Box
                  key={p.title}
                  style={{
                    ...splashCard,
                    padding: 22,
                    flex: "1 1 280px",
                    minWidth: 260,
                    maxWidth: 380,
                    borderTop: `3px solid ${p.accent}`,
                    boxShadow: "0 14px 32px -14px rgba(15,23,42,.22)",
                  }}
                >
                  <Snippet />
                  <div style={{ fontSize: 18, fontWeight: 600, color: SPLASH_INK, marginBottom: 8, padding: "0 4px" }}>{p.title}</div>
                  <div style={{ fontSize: 14, color: SPLASH_MUTE, lineHeight: 1.6, padding: "0 4px" }}>{p.body}</div>
                </Box>
              );
            })}
          </Box>
          <div style={{ textAlign: "center", marginTop: 36 }}>
            <a href="/features" style={{ fontSize: 14, fontWeight: 600, color: SPLASH_BLUE, textDecoration: "none" }}>
              See everything Swyvel does →
            </a>
          </div>
        </Container>
      </Box>

      {/* Bottom CTA */}
      <Box
        style={{
          background: "#FAFAFA",
          borderTop: `1px solid ${SPLASH_LINE}`,
          textAlign: "center",
          padding: "72px 20px",
        }}
      >
        <Typography style={{ fontSize: 30, fontWeight: 600, color: SPLASH_INK, marginBottom: 10 }}>
          Ready to run your studio from the floor, not the desk?
        </Typography>
        <Typography style={{ fontSize: 16, color: SPLASH_MUTE, marginBottom: 26 }}>
          Set up your studio in minutes — bring your classes, families, and schedule.
        </Typography>
        <Box style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <button style={splashPrimaryCTA} onClick={() => startTrial("homepage_bottom_cta")}>
            Start free
          </button>
          <button style={splashSecondaryCTA} onClick={goLogin}>
            Log in
          </button>
        </Box>
        <div style={{ fontSize: 13, color: SPLASH_MUTE, marginTop: 14 }}>
          14 days free · no credit card required
        </div>
      </Box>
    </Box>
  );
};

// Self-contained wrapper: MUI providers + the splash content.
const SplashLanding = (props) => (
  <StylesProvider generateClassName={splashGenerateClassName}>
    <ThemeProvider theme={splashTheme}>
      <SplashLandingContent {...props} />
    </ThemeProvider>
  </StylesProvider>
);

export default SplashLanding;
